/**
 * Florida retail sales tax for a motor vehicle.
 *
 * This mirrors the generated columns in the deals table. The database is
 * authoritative — these figures are for showing the itemization live while
 * someone types. If the two ever disagree, the database is right and this
 * file has a bug.
 *
 * Rules, from the FL DOR:
 *   - 6% state rate on the taxable amount
 *   - county discretionary surtax on the FIRST $5,000 only
 *   - the county is the BUYER'S, from their address, not the lot's
 *   - a trade-in allowance reduces the taxable amount
 *   - a trade-in payoff is added after tax and is not taxable
 *   - fees mandated by the state for title, license, registration are not taxable
 */

import type { Cents } from "./money";

export const STATE_TAX_BPS = 600;         // 6.00%
export const SURTAX_CAP_CENTS = 500_000;  // first $5,000

export interface DealAmounts {
  cashPrice: Cents;
  predeliveryService: Cents;   // taxable dealer charge
  electronicFiling: Cents;     // taxable dealer charge
  licenseRegistration: Cents;  // government, not taxable
  titleFee: Cents;             // government, not taxable
  tempTag: Cents;              // government at cost, not taxable
  tradeAllowance: Cents;
  tradePayoff: Cents;
  cashDown: Cents;
  deferredDown: Cents;
  countySurtaxBps: number;
}

export interface DealTotals {
  taxableAmount: Cents;
  stateTax: Cents;
  surtax: Cents;
  totalSalesPrice: Cents;
  totalAmountDue: Cents;
  unpaidBalance: Cents;
  surtaxWasCapped: boolean;
}

/** Round half away from zero, matching Postgres round() on numeric. */
const round = (n: number): Cents => Math.sign(n) * Math.round(Math.abs(n));

export function computeDeal(a: DealAmounts): DealTotals {
  const taxableAmount = Math.max(
    0,
    a.cashPrice + a.predeliveryService + a.electronicFiling - a.tradeAllowance,
  );

  const stateTax = round((taxableAmount * STATE_TAX_BPS) / 10_000);
  const surtaxBase = Math.min(taxableAmount, SURTAX_CAP_CENTS);
  const surtax = round((surtaxBase * a.countySurtaxBps) / 10_000);

  const totalSalesPrice = taxableAmount + stateTax + surtax;
  const totalAmountDue =
    totalSalesPrice + a.licenseRegistration + a.titleFee + a.tempTag + a.tradePayoff;
  const unpaidBalance = totalAmountDue - a.cashDown - a.deferredDown;

  return {
    taxableAmount,
    stateTax,
    surtax,
    totalSalesPrice,
    totalAmountDue,
    unpaidBalance,
    surtaxWasCapped: taxableAmount > SURTAX_CAP_CENTS,
  };
}

export const emptyDeal = (countySurtaxBps = 0): DealAmounts => ({
  cashPrice: 0, predeliveryService: 0, electronicFiling: 0,
  licenseRegistration: 0, titleFee: 0, tempTag: 0,
  tradeAllowance: 0, tradePayoff: 0, cashDown: 0, deferredDown: 0,
  countySurtaxBps,
});
